import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import { ROUTES } from 'src/constants/routes.const';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { MeApi, UserDictionaryApi } from 'src/api';
import { API_TARGET_ITEM_TYPES } from 'src/constants/itemTypes';
import { Lesson, RecommendedLesson } from '../types';

const msgs = defineMessages({
  noLearn: { id: 'learnq_endState_message_noLearn' },
  noReview: { id: 'learnq_endState_message_noReview' },
  noVideo: { id: 'learnq_endState_message_noVideos' },
  noConversations: { id: 'learnq_endState_message_noConversations' },
  noPronunciation: { id: 'learnq_endState_message_noPronunciation' },
  learnWords: { id: 'learnq_widget_title_learn' },
});

export const useRecommendedLesson = (lesson: Lesson | undefined) => {
  const languagePairId = getLanguagePairId();
  const intl = useIntl();

  const { data: recommendations, isError: isErrorRecommendations } = useQuery({
    queryKey: ['recommendations', languagePairId],
    queryFn: () => MeApi.getRecommendations(languagePairId as number),
    enabled:
      !!languagePairId &&
      (lesson?.type === 'learn' ||
        lesson?.type === 'pronunciation' ||
        lesson?.type === 'video' ||
        lesson?.type === 'conversation'),
  });

  const { data: reviewData, isError: isErrorReview } = useQuery({
    queryKey: ['practice', languagePairId],
    queryFn: () => MeApi.getPractice(languagePairId as number),
    enabled: !!languagePairId && lesson?.type === 'review',
  });

  const { data: pronunciationData, isError: isErrorPronunciation } = useQuery({
    queryKey: ['recent_learnables', languagePairId],
    queryFn: () =>
      UserDictionaryApi.getRecentLearnables(languagePairId as number, [
        API_TARGET_ITEM_TYPES.ALPHABET,
      ]),
    enabled: !!languagePairId && lesson?.type === 'pronunciation',
  });

  const recommendedLesson = useMemo((): RecommendedLesson | null => {
    if (!lesson) return null;

    const languagePairId = getLanguagePairId();
    /* istanbul ignore if: should be defined */
    if (!languagePairId) return null;

    const backUrl = encodeURIComponent(ROUTES.myLessons);

    switch (lesson.type) {
      // eslint-disable-next-line no-warning-comments
      // TODO: moving forward learn should be based on active wordlist
      case 'learn': {
        if (!recommendations) return null;

        if (!recommendations.learn?.nextScenario) {
          return {
            ...lesson,
            enabled: false,
            disabledReason: intl.formatMessage(msgs.noLearn),
          };
        }

        return {
          ...lesson,
          enabled: true,
          url: `${ROUTES.aprender.learn}?scenario_id=${recommendations.learn.nextScenario.scenarioId}&back=${backUrl}`,
        };
      }

      case 'review': {
        if (!reviewData) return null;

        if (!reviewData.difficultWords.enabled && !reviewData.review.enabled) {
          return {
            ...lesson,
            enabled: false,
            disabledReason: intl.formatMessage(msgs.noReview),
          };
        }

        return {
          ...lesson,
          enabled: true,
          url: `${ROUTES.aprender.review}?language_pair_id=${languagePairId}&back=${backUrl}`,
        };
      }

      case 'video': {
        if (!recommendations) return null;

        if (!recommendations.immerse) {
          return {
            ...lesson,
            enabled: false,
            disabledReason: intl.formatMessage(msgs.noVideo),
          };
        }

        return {
          ...lesson,
          enabled: true,
          url: `${ROUTES.video.media}?contentMediaId=${recommendations.immerse.contentMediaId}&languagePairId=${languagePairId}&back=${backUrl}`,
        };
      }

      case 'conversation': {
        if (!recommendations) return null;

        if (!recommendations.communicate) {
          return {
            ...lesson,
            enabled: false,
            disabledReason: intl.formatMessage(msgs.noConversations),
          };
        }

        return {
          ...lesson,
          enabled: true,
          url: `${ROUTES.membot.dialog}?slug=${recommendations.communicate.locationSlug}&language_pair=${languagePairId}&back=${backUrl}`,
        };
      }

      case 'sentence_builder':
        return {
          ...lesson,
          enabled: true,
          url: `${ROUTES.ai.dialog}?type=sentence-builder&back=${backUrl}`,
        };

      case 'pronunciation': {
        if (!pronunciationData || !recommendations) return null;

        if (pronunciationData.learnables.length === 0) {
          return {
            ...lesson,
            enabled: false,
            disabledReason: intl.formatMessage(msgs.noPronunciation),
            alternativeLesson: recommendations.learn?.nextScenario
              ? {
                  ctaText: intl.formatMessage(msgs.learnWords),
                  url: `${ROUTES.aprender.learn}?scenario_id=${recommendations.learn.nextScenario.scenarioId}&back=${backUrl}`,
                }
              : undefined,
          };
        }

        return {
          ...lesson,
          enabled: true,
          url: `${ROUTES.ai.dialog}?type=pronunciation&back=${backUrl}`,
        };
      }
    }
  }, [intl, lesson, recommendations, reviewData, pronunciationData]);

  const isError = isErrorRecommendations || isErrorReview || isErrorPronunciation;

  return {
    recommendedLesson,
    isLoading: lesson === undefined ? false : !isError && !recommendedLesson,
    isError,
  };
};
